/**
 * AI 업무 자동화 수요 조사 설문(/survey) — 문항과 보기.
 *
 * 문구는 기존 설문(www.autop.kr/survey)의 원문 그대로다. 보기 글자가 곧 저장되는 값이라
 * (서버는 목록과 대조하지 않는다 — api 의 crm/dto/survey.schema.ts), 보기를 고치면 이미 쌓인
 * 응답과 글자가 달라진다. 오타 수정이 아니라면 설문을 연 뒤에는 보기를 바꾸지 않는다.
 */

/** 「기타」를 고르면 내용을 적는 칸이 열린다. 서버의 OTHER_CHOICE 와 같은 글자다. */
export const SURVEY_OTHER = '기타';

/** 2·5번의 「최대 3개」. */
export const SURVEY_MAX_PICKS = 3;

/** 7번 업무 이야기 상한 — 서버와 같은 값. */
export const SURVEY_STORY_MAX_LENGTH = 2000;

/**
 * 설문 참여 안내 띠를 다는 영상 — 「AI 상세페이지 만드는 법」(유튜브 ID).
 *
 * 영상 주소(slug)가 아니라 유튜브 ID 로 고른다. slug 는 어드민에서 바뀔 수 있지만
 * 유튜브 ID 는 영상이 같은 한 바뀌지 않는다. 일회성 설문이라 어드민 설정을 두지 않았다.
 */
export const SURVEY_VIDEO_YOUTUBE_ID = 'iibHwQQswik';

/** 고르는 문항의 답 칸 이름 — 서버가 받는 answers 의 키와 같다. */
export type SurveyChoiceKey =
  | 'role'
  | 'timeSpent'
  | 'tools'
  | 'aiExperience'
  | 'wantCases'
  | 'reason';

export interface SurveyChoiceQuestion {
  key: SurveyChoiceKey;
  /** 화면에 보이는 번호. */
  number: number;
  title: string;
  hint?: string;
  /** single = 하나만, multi = 여러 개 */
  kind: 'single' | 'multi';
  /** multi 에서 고를 수 있는 최대 개수. 없으면 제한 없음. */
  maxPicks?: number;
  required: boolean;
  options: readonly string[];
}

export const SURVEY_ROLE: SurveyChoiceQuestion = {
  key: 'role',
  number: 1,
  title: '현재 어떤 일을 하고 계신가요?',
  kind: 'single',
  required: true,
  options: [
    '직장인 — 사무/운영',
    '직장인 — 마케팅/기획',
    '직장인 — 영업/고객관리',
    '대표/사업자',
    '프리랜서/1인 사업자',
    '크리에이터/콘텐츠 제작자',
    '개발자/IT 직군',
    '학생/취준생',
    SURVEY_OTHER,
  ],
};

export const SURVEY_TIME_SPENT: SurveyChoiceQuestion = {
  key: 'timeSpent',
  number: 2,
  title: '현재 업무에서 가장 시간을 많이 쓰는 일은 무엇인가요?',
  hint: '최대 3개까지 선택해주세요.',
  kind: 'multi',
  maxPicks: SURVEY_MAX_PICKS,
  required: true,
  options: [
    '문서 작성 / 보고서 작성',
    '엑셀 / 스프레드시트 정리',
    '이메일 작성 / 회신',
    '메신저 알림 / 업무 전달',
    '회의록 정리 / 요약',
    '일정 관리 / 리마인드',
    '자료 조사 / 리서치',
    '콘텐츠 기획 / 제작',
    '고객 문의 / CS 응대',
    '매출·광고 데이터 정리',
    SURVEY_OTHER,
  ],
};

export const SURVEY_TOOLS: SurveyChoiceQuestion = {
  key: 'tools',
  number: 3,
  title: '지금 사용 중인 업무 툴이 있다면 선택해주세요.',
  hint: '복수 선택 가능합니다.',
  kind: 'multi',
  required: false,
  options: [
    'Slack',
    'Notion',
    'Google Sheets',
    'Google Drive',
    'Gmail',
    'Google Calendar',
    'Microsoft Excel',
    'Microsoft Teams',
    '카카오톡 / 오픈채팅',
    '네이버 메일 / 네이버웍스',
    'CRM / 고객관리 툴',
    '별도로 사용하는 툴 없음',
    SURVEY_OTHER,
  ],
};

export const SURVEY_AI_EXPERIENCE: SurveyChoiceQuestion = {
  key: 'aiExperience',
  number: 4,
  title: 'AI 또는 자동화 툴을 사용해본 경험이 있나요?',
  kind: 'single',
  required: true,
  options: [
    '아직 거의 안 써봤어요',
    'ChatGPT·Claude 같은 AI 챗봇에 가끔 질문해요',
    'AI 챗봇을 업무에 적극적으로 활용해요 (문서·기획·코드 등)',
    'AI가 직접 작업까지 해주는 도구를 써봤어요 (Claude Code·Cowork 등)',
    'Make·n8n·Zapier 같은 자동화 툴로 워크플로우를 만들어봤어요',
  ],
};

export const SURVEY_WANT_CASES: SurveyChoiceQuestion = {
  key: 'wantCases',
  number: 5,
  title: '가장 배우고 싶은 자동화 사례는 무엇인가요?',
  hint: '최대 3개까지 선택해주세요.',
  kind: 'multi',
  maxPicks: SURVEY_MAX_PICKS,
  required: true,
  options: [
    '메일을 자동 분류·요약하고 답장 초안까지 만들기',
    '고객 문의(CS)를 자동 분류하고 답변 초안 만들기',
    '회의록을 자동 요약해 할 일·Notion으로 정리하기',
    '엑셀/스프레드시트 데이터를 자동 수집·정리하기',
    '매일·매주 반복 리포트를 자동 생성해 메일·슬랙으로 발송하기',
    '광고·마케팅 데이터를 모아 리포트·대시보드 만들기',
    '나만의 업무용 GPT(맞춤 챗봇·지침 프롬프트) 만들기',
    '우리 회사 문서를 학습한 AI 챗봇 만들기',
    '웹·경쟁사 정보를 자동으로 수집·요약하기 (리서치 자동화)',
    '여러 앱(Slack·Notion·Sheets·Gmail)을 연결한 업무 파이프라인 만들기',
    '여러 업무를 한 번에 묶어 자동화하기 (n8n·Make)',
    SURVEY_OTHER,
  ],
};

export const SURVEY_REASON: SurveyChoiceQuestion = {
  key: 'reason',
  number: 6,
  title: '업무 자동화를 배우고 싶은 가장 큰 이유는 무엇인가요?',
  kind: 'single',
  required: true,
  options: [
    '반복 업무 시간을 줄이고 싶어서',
    'AI를 어디에 써야 할지 막막해서',
    '회사/팀 업무 효율을 높이고 싶어서',
    '혼자 처리하는 업무가 너무 많아서',
    '실무에 바로 적용 가능한 예제가 필요해서',
    '자동화 툴이 어려워 보여 엄두가 안 나서',
    '새로운 업무 역량을 만들고 싶어서',
    SURVEY_OTHER,
  ],
};

/** 화면의 세 묶음. 7~9번은 입력 형태가 달라 페이지가 직접 그린다. */
export const SURVEY_SECTIONS = [
  { title: '현재 업무에 대해', questions: [SURVEY_ROLE, SURVEY_TIME_SPENT, SURVEY_TOOLS] },
  {
    title: '자동화, 무엇을 배우고 싶나요?',
    questions: [SURVEY_AI_EXPERIENCE, SURVEY_WANT_CASES, SURVEY_REASON],
  },
] as const;

/** 9번 — 1:1 무료 컨설팅 추첨 응모. */
export const SURVEY_CONSULTING_OPTIONS = [
  { value: 'yes', label: '네, 응모할게요' },
  { value: 'no', label: '괜찮아요, 자료만 받을게요' },
] as const;
